import React from "react";
import { Col } from "react-bootstrap";

import Control from "../../../assets/vfx/controls/control";
import Form from "../../../assets/vfx/form/form";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";

import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, sortBy } from "../../../assets/types";

import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { GridClaimDto, SearchClaimDto } from "../../../services/dto";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import { ClaimService } from "./claimService";
import { LookupDto } from "../../../services/dto/account/lookupDto";

interface ClaimPageProps {
  claimTypes: LookupDto[];
  controlStyles?: IControlStyles;
  onCustomEvent?: (actionType: string, dataId: string) => void;
}

const DATA_ID_FIELD = "id";

const searchInitialValues: SearchClaimDto = {
  name: Constants.emptyString,
  pageSize: 10,
  pageNumber: 1,
  sortBy,
  typeId: undefined,
};

const createGridColumns = (): ControlDataGridColumnProps[] => [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    data: "name",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "18%",
  },
  {
    data: "description",
    title: "Description",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "34%",
  },
  {
    data: "claimType",
    title: "Claim Type",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "24%",
  },
];

export const convertToDropdownOptions = (
  claimTypes: LookupDto[]
): DropdownOption[] => {
  return claimTypes.map((claimType) => ({
    key: claimType.id,
    value: claimType.id,
    display: claimType.name ?? Constants.emptyString,
  }));
};

export const ClaimPage = (props: ClaimPageProps) => {
  const styles = props.controlStyles ?? useControlStyles();
  const [searchQuery, setSearchQuery] =
    React.useState<SearchClaimDto>(searchInitialValues);

  const claimGridQuery = ClaimService.gridQuery(searchQuery);
  const isGridLoading = claimGridQuery.isLoading || claimGridQuery.isFetching;

  const claims: GridClaimDto[] = claimGridQuery.data?.result ?? [];
  const hasData: boolean = claims.length > 0;
  const gridColumns: ControlDataGridColumnProps[] = createGridColumns();
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
  ]);
  const layoutOptions = createLayoutOptions(hasData);
  const dropdownOptions: DropdownOption[] = convertToDropdownOptions(
    props.claimTypes
  );

  const handleSearch = (values: any) => {
    const hasName = values.name.trim() !== Constants.emptyString;
    const hasTypeId = values.typeId !== Constants.emptyGuid;

    if (hasName || hasTypeId) {
      setSearchQuery({
        name: values.name,
        typeId: hasTypeId ? values.typeId : undefined,
        pageSize: 10,
        pageNumber: 1,
        sortBy: sortBy,
      });
    }
  };

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...searchInitialValues,
      sortBy: prev.sortBy,
      pageSize: prev.pageSize ?? 10,
    }));
  };

  const handleNew = (): void =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  return (
    <PageContent title="Claims">
      <SearchContainer
        initialValues={{ ...searchQuery, typeId: Constants.emptyGuid }}
        onSearch={handleSearch}
        onNew={handleNew}
        onClear={handleClear}
        enableReinitialize
      >
        <Col lg={4}>
          <Form.Text name="name" label="Name" />
        </Col>
        <Col lg={3} className="ms-2">
          <Form.Dropdown
            blankOption={{
              key: Constants.emptyGuid,
              value: Constants.emptyGuid,
              display: "Select Claim Type",
            }}
            options={dropdownOptions}
            name={"typeId"}
            label={"Claim Type"}
          ></Form.Dropdown>
        </Col>
      </SearchContainer>
      <Control.DataGrid
        dataId={DATA_ID_FIELD}
        cols={gridColumns}
        data={claims}
        actionCol={actionColumn}
        layoutOptions={layoutOptions}
        onCustomEvent={props.onCustomEvent}
        totalRecords={claimGridQuery.data?.totalSize ?? claims?.length ?? 0}
        query={searchQuery}
        loading={isGridLoading}
        onQueryChange={(patch) =>
          setSearchQuery((query) => ({ ...query, ...patch }))
        }
      />
    </PageContent>
  );
};
